<template>
  <v-card flat class="pa-4 purchase-details">
    <!-- Purchase Header Section -->
    <v-card outlined rounded="lg" class="mb-4">
      <v-card-title class="primary--text">
        {{ $t("purchase_details") }}
        <v-spacer></v-spacer>
        <v-chip
          color="primary"
          dark
          label
          small
          class="font-weight-bold purchase-code"
        >
          {{ item.code }}
        </v-chip>
        <v-btn
          fab
          class="rounded-lg ml-2"
          depressed
          x-small
          @click="copyToClipboard(item.code)"
          title="Copy purchase number"
        >
          <v-icon x-small>fal fa-copy</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider class="my-0" />

      <v-card-text>
        <v-row>
          <!-- Purchase Information -->
          <v-col cols="12" md="7" lg="8">
            <v-row class="info-grid">
              <v-col cols="12" sm="6">
                <v-row dense>
                  <v-col cols="5" class="info-label">
                    {{ $t("branch") }}:
                  </v-col>
                  <v-col cols="7" class="font-weight-medium">
                    {{ item.branch ? item.branch.name : "-" }}
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="5" class="info-label">
                    {{ $t("maker") }}:
                  </v-col>
                  <v-col cols="7" class="font-weight-medium">
                    {{
                      item.user
                        ? `${item.user.first_name} ${item.user.last_name}`
                        : "-"
                    }}
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="5" class="info-label">
                    {{ $t("product_item") }}:
                  </v-col>
                  <v-col cols="7" class="font-weight-medium">
                    {{ item.product_count || item.stock_history_count }}
                    {{ $t("item") }}
                    <v-chip
                      v-if="item.has_variants"
                      x-small
                      color="info"
                      class="ml-2"
                    >
                      {{ item.variant_count | ToNumber }} {{ $t("variants") }}
                    </v-chip>
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="5" class="info-label">
                    {{ $t("total_quantity") }}:
                  </v-col>
                  <v-col cols="7" class="font-weight-medium">
                    {{ item.total_qty || 0 | ToNumber }}
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" sm="6">
                <v-row dense>
                  <v-col cols="5" class="info-label">
                    {{ $t("created_at") }}:
                  </v-col>
                  <v-col cols="7" class="font-weight-medium">
                    {{ item.created_at | moment }}
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="5" class="info-label">
                    {{ $t("profit_percent") }}:
                  </v-col>
                  <v-col cols="7" class="font-weight-medium">
                    <v-chip
                      x-small
                      :color="getProfitColor(item.profit_margin)"
                      text-color="white"
                      class="font-weight-medium"
                    >
                      {{ item.profit_margin || 0 }}%
                    </v-chip>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="5" class="info-label">
                    {{ $t("status") }}:
                  </v-col>
                  <v-col cols="7">
                    <v-chip
                      x-small
                      :color="getStatusColor(item.status)"
                      text-color="white"
                      class="font-weight-medium"
                    >
                      {{ $t(item.status) }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>

          <!-- Summary Card -->
          <v-col cols="12" md="5" lg="4">
            <v-card outlined class="summary-card">
              <v-list-item three-line>
                <v-list-item-content>
                  <div class="summary-item">
                    <div class="summary-label">{{ $t("total_buy") }}</div>
                    <div class="summary-value red--text">
                      {{ item.total_cost | NumberFormat }}
                    </div>
                  </div>

                  <div class="summary-item">
                    <div class="summary-label">{{ $t("total_sale") }}</div>
                    <div class="summary-value primary--text">
                      {{ item.total_value | NumberFormat }}
                    </div>
                  </div>

                  <div class="summary-item">
                    <div class="summary-label">{{ $t("total_profit") }}</div>
                    <div class="summary-value green--text font-weight-bold">
                      {{ (item.total_value - item.total_cost) | NumberFormat }}
                    </div>
                  </div>

                  <div class="summary-item">
                    <div class="summary-label">{{ $t("total_quantity") }}</div>
                    <div class="summary-value blue--text font-weight-bold">
                      {{ item.total_qty || 0 }}
                    </div>
                  </div>
                </v-list-item-content>
              </v-list-item>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Items Section -->
    <v-card outlined rounded="lg">
      <v-card-title class="items-title">
        {{ $t("purchased_items") }}
        <v-spacer></v-spacer>
        <v-chip v-if="item.has_variants" color="info" small class="mr-2">
          {{ item.variant_count | ToNumber }} {{ $t("variants") }}
        </v-chip>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-expansion-panels>
          <v-expansion-panel
            v-for="(data, index) in item.history"
            :key="data.id"
            class="history-panel"
          >
            <v-expansion-panel-header class="history-header">
              <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center">
                  <div class="mr-3 history-number">#{{ index + 1 }}</div>
                  <div class="d-flex align-center">
                    <v-avatar
                      tile
                      size="40"
                      class="rounded-lg mr-3"
                      color="grey lighten-2"
                    >
                      <app-img :img="data.product.img" />
                    </v-avatar>
                    <div>
                      <div class="product-name font-weight-medium">
                        {{ data.product.name | nameLimit }}
                      </div>
                      <div class="product-code text-caption grey--text">
                        {{ data.product.code }}
                      </div>
                      <div
                        v-if="data.product.category"
                        class="product-category text-caption"
                      >
                        {{ data.product.category.la_name }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="d-flex align-center">
                  <div class="text-right mr-4">
                    <div class="history-qty font-weight-bold primary--text">
                      {{ data.qty }} {{ $t("qty") }}
                    </div>
                    <div class="history-price text-caption grey--text">
                      {{ $t("price") }}: {{ data.price_sale | NumberFormat }}
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="history-total font-weight-bold">
                      {{ data.total_value | NumberFormat }}
                    </div>
                    <div class="history-profit text-caption">
                      <v-chip
                        x-small
                        :color="getProfitColor(data.profit_margin)"
                        text-color="white"
                      >
                        {{ data.profit_margin }}%
                      </v-chip>
                    </div>
                  </div>
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="history-content">
              <v-row>
                <!-- Basic Information -->
                <v-col cols="12" md="6">
                  <div class="detail-section">
                    <h4 class="detail-title">
                      <v-icon small class="mr-2">fal fa-info-circle</v-icon>
                      {{ $t("basic_info") }}
                    </h4>
                    <div class="detail-grid">
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ $t("product_name") }}:</span
                        >
                        <span class="detail-value">{{
                          data.product.name
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ $t("product_code") }}:</span
                        >
                        <span class="detail-value font-family-monospace">
                          {{ data.product.code }}
                          <v-btn
                            icon
                            small
                            class="ml-2"
                            @click="copyToClipboard(data.product.code)"
                          >
                            <v-icon small>fal fa-copy</v-icon>
                          </v-btn>
                        </span>
                      </div>
                      <div class="detail-item" v-if="data.product.category">
                        <span class="detail-label">{{ $t("category") }}:</span>
                        <span class="detail-value">{{
                          data.product.category.la_name
                        }}</span>
                      </div>
                      <div class="detail-item" v-if="data.product.size">
                        <span class="detail-label">{{ $t("unit") }}:</span>
                        <span class="detail-value">{{
                          data.product.size.name
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">{{ $t("currency") }}:</span>
                        <span class="detail-value">{{
                          data.currency.code
                        }}</span>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Pricing Information -->
                <v-col cols="12" md="6">
                  <div class="detail-section">
                    <h4 class="detail-title">
                      <v-icon small class="mr-2">fal fa-dollar-sign</v-icon>
                      {{ $t("pricing_info") }}
                    </h4>
                    <div class="detail-grid">
                      <div class="detail-item">
                        <span class="detail-label">{{ $t("price_buy") }}:</span>
                        <span class="detail-value red--text font-weight-medium">
                          {{ data.price_buy | NumberFormat }}
                        </span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ $t("price_sale") }}:</span
                        >
                        <span
                          class="detail-value primary--text font-weight-medium"
                        >
                          {{ data.price_sale | NumberFormat }}
                        </span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">{{ $t("quantity") }}:</span>
                        <span class="detail-value">{{
                          data.qty | ToNumber
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ $t("total_cost") }}:</span
                        >
                        <span class="detail-value red--text font-weight-bold">
                          {{ data.total_cost | NumberFormat }}
                        </span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ $t("total_value") }}:</span
                        >
                        <span
                          class="detail-value primary--text font-weight-bold"
                        >
                          {{ data.total_value | NumberFormat }}
                        </span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ $t("profit_margin") }}:</span
                        >
                        <span class="detail-value">
                          <v-chip
                            small
                            :color="getProfitColor(data.profit_margin)"
                            text-color="white"
                          >
                            {{ data.profit_margin }}%
                          </v-chip>
                        </span>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Variant Information -->
                <v-col cols="12" v-if="data.variant">
                  <div class="detail-section">
                    <h4 class="detail-title">
                      <v-icon small class="mr-2">fal fa-cubes</v-icon>
                      {{ $t("variant_information") }}
                    </h4>
                    <div class="detail-grid">
                      <div class="detail-item">
                        <span class="detail-label">{{ $t("sku") }}:</span>
                        <span class="detail-value font-family-monospace">
                          {{ data.variant.sku }}
                          <v-btn
                            icon
                            small
                            class="ml-2"
                            @click="copyToClipboard(data.variant.sku)"
                          >
                            <v-icon small>fal fa-copy</v-icon>
                          </v-btn>
                        </span>
                      </div>

                      <div class="detail-item">
                        <span class="detail-label">{{ $t("cost") }}:</span>
                        <span class="detail-value">{{
                          data.variant.cost | NumberFormat
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">{{ $t("price") }}:</span>
                        <span class="detail-value">{{
                          data.variant.price | NumberFormat
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">{{ $t("stock") }}:</span>
                        <span class="detail-value">{{
                          data.variant.stock | ToNumber
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">{{ $t("status") }}:</span>
                        <span class="detail-value">
                          <v-chip
                            x-small
                            :color="data.variant.is_active ? 'success' : 'grey'"
                            text-color="white"
                          >
                            {{
                              data.variant.is_active
                                ? $t("active")
                                : $t("inactive")
                            }}
                          </v-chip>
                        </span>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Stock Information -->
                <v-col cols="12" md="6">
                  <div class="detail-section">
                    <h4 class="detail-title">
                      <v-icon small class="mr-2">fal fa-warehouse</v-icon>
                      {{ $t("stock_information") }}
                    </h4>
                    <div class="detail-grid">
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ $t("last_stock") }}:</span
                        >
                        <span class="detail-value">{{
                          data.last_stock | NumberFormat
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ $t("current_stock") }}:</span
                        >
                        <span class="detail-value">{{
                          data.current_stock | NumberFormat
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ $t("stock_change") }}:</span
                        >
                        <span class="detail-value">
                          <v-chip
                            x-small
                            :color="
                              data.stock_change >= 0 ? 'success' : 'warning'
                            "
                            text-color="white"
                          >
                            {{ data.stock_change >= 0 ? "+" : ""
                            }}{{ data.stock_change }}
                          </v-chip>
                        </span>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Additional Information -->
                <v-col cols="12" md="6">
                  <div class="detail-section">
                    <h4 class="detail-title">
                      <v-icon small class="mr-2">fal fa-clock</v-icon>
                      {{ $t("additional_information") }}
                    </h4>
                    <div class="detail-grid">
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ $t("created_at") }}:</span
                        >
                        <span class="detail-value">{{
                          data.created_at | moment
                        }}</span>
                      </div>
                      <div class="detail-item" v-if="data.expired_date">
                        <span class="detail-label"
                          >{{ $t("expired_date") }}:</span
                        >
                        <span class="detail-value">{{
                          data.expired_date | moment
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ $t("exchange_rate") }}:</span
                        >
                        <span class="detail-value">{{
                          data.exchange_rate | NumberFormat
                        }}</span>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <div class="currency-breakdown pt-0" v-if="currencySummary.length > 1">
      <div class="breakdown-title">{{ $t("currency_breakdown") }}</div>
      <div class="breakdown-grid">
        <div
          v-for="(summary, currency) in currencySummary"
          :key="currency"
          class="breakdown-card"
        >
          <div class="breakdown-header">{{ currency }}</div>
          <div class="breakdown-content">
            <div class="breakdown-row">
              <span>{{ $t("quantity") }}:</span>
              <span>{{ summary.qty | ToNumber }}</span>
            </div>
            <div class="breakdown-row">
              <span>{{ $t("price_buy") }}:</span>
              <span class="red--text">{{
                summary.price_buy | NumberFormat
              }}</span>
            </div>
            <div class="breakdown-row">
              <span>{{ $t("price_sale") }}:</span>
              <span class="primary--text">{{
                summary.price_sale | NumberFormat
              }}</span>
            </div>
            <div class="breakdown-row">
              <span>{{ $t("profit") }}:</span>
              <span :class="summary.profit > 0 ? 'green--text' : 'red--text'">
                {{ summary.profit | NumberFormat }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import { myMixin } from "@/mixin/main";

export default {
  mixins: [myMixin],
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      search: "",
      loading: false,
      headers: [
        { text: this.$t("no"), value: "index", align: "center", width: "50px" },
        {
          text: this.$t("image"),
          value: "image",
          align: "center",
          sortable: false,
        },
        { text: this.$t("product"), value: "product.name", align: "left" },
        {
          text: this.$t("unit"),
          value: "product.size.name",
          align: "center",
        },
        {
          text: this.$t("currency"),
          value: "currency.code",
          align: "center",
        },
        {
          text: this.$t("exchange_rate"),
          value: "exchange_rate",
          align: "right",
        },
        {
          text: this.$t("price_buy"),
          value: "price_buy",
          align: "right",
        },
        {
          text: this.$t("price_sale"),
          value: "price_sale",
          align: "right",
        },
        {
          text: this.$t("quantity"),
          value: "qty",
          align: "center",
          width: "80px",
        },
        {
          text: this.$t("total_buy"),
          value: "total_buy",
          align: "right",
        },
        {
          text: this.$t("total_sale"),
          value: "total_sale",
          align: "right",
        },
        {
          text: this.$t("profit"),
          value: "profit",
          align: "right",
          width: "100px",
        },
        {
          text: this.$t("action"),
          value: "actions",
          align: "center",
          sortable: false,
        },
      ],
    };
  },
  computed: {
    total_summary() {
      // Use the new computed fields from the API response
      return {
        price_buy: this.item.total_cost || 0,
        price_sale: this.item.total_value || 0,
        profit: (this.item.total_value || 0) - (this.item.total_cost || 0),
        qty: this.item.total_qty || 0,
        percent: this.item.profit_margin || 0,
      };
    },

    profitPercentage() {
      if (this.item.total_cost === 0) return 0;
      return this.item.profit_margin || 0;
    },

    profitPercentageColor() {
      const percentage = this.item.profit_margin || 0;
      if (percentage > 20) return "green--text";
      if (percentage > 0) return "light-green--text";
      if (percentage < 0) return "red--text";
      return "grey--text";
    },

    currencySummary() {
      const summary = {};

      if (this.item && this.item.history) {
        this.item.history.forEach((item) => {
          const currency = item.currency.code;

          if (!summary[currency]) {
            summary[currency] = {
              price_buy: 0,
              price_sale: 0,
              qty: 0,
              profit: 0,
            };
          }

          summary[currency].qty += parseFloat(item.qty);
          summary[currency].price_buy += item.total_cost || 0;
          summary[currency].price_sale += item.total_value || 0;
          summary[currency].profit +=
            (item.total_value || 0) - (item.total_cost || 0);
        });
      }

      return summary;
    },
  },
  methods: {
    viewItem(data) {
      this.$emit("viewItem", data);
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(
        () => {
          this.$toast.success(this.$t("copied_to_clipboard"));
        },
        () => {
          this.$toast.error(this.$t("copy_failed"));
        }
      );
    },
    getStatusColor(status) {
      const statusColors = {
        pending: "orange",
        completed: "success",
        cancelled: "error",
        processing: "primary",
      };
      return statusColors[status] || "grey";
    },
    getProfitColor(margin) {
      if (margin > 20) return "success";
      if (margin > 0) return "light-green";
      if (margin < 0) return "error";
      return "grey";
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
  },
  filters: {
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    nameLimit(name) {
      if (!name) return "";
      return name.length > 30 ? name.substring(0, 30) + "..." : name;
    },
  },
};
</script>

<style scoped>
.purchase-details {
  /* Main container styles */
  background-color: #f8f9fa;
}

.purchase-code {
  font-family: monospace;
  letter-spacing: 0.5px;
}

.info-grid {
  margin-top: 8px;
}

.info-label {
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
}

/* Summary card styling */
.summary-card {
  background-color: #f5f7fa;
  border-radius: 8px;
  height: 100%;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px dashed #e0e0e0;
}

.summary-label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
}

.summary-value {
  font-size: 1.1rem;
  font-weight: 600;
}

/* Table styling */
.items-title {
  display: flex;
  align-items: center;
}

.search-field {
  max-width: 250px;
}

/* Expansion Panel Styling */
.history-panel {
  margin-bottom: 8px;
}

.history-header {
  padding: 16px;
  background-color: #f8f9fa;
}

.history-number {
  font-weight: 600;
  color: #5b39c9;
  font-size: 1.1rem;
}

.history-qty {
  font-size: 1.2rem;
}

.history-price {
  margin-top: 4px;
}

.history-total {
  font-size: 1.1rem;
  color: #5b39c9;
}

.history-profit {
  margin-top: 4px;
}

.history-content {
  padding: 20px;
  background-color: white;
}

.detail-section {
  margin-bottom: 24px;
}

.detail-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
  display: flex;
  align-items: center;
}

.detail-grid {
  display: grid;
  gap: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px dashed #f0f0f0;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
  min-width: 120px;
}

.detail-value {
  text-align: right;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.9);
  max-width: 200px;
  word-break: break-word;
}

.font-family-monospace {
  font-family: "Courier New", monospace;
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9rem;
}

/* Product Information */
.product-name {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.9);
}

.product-code {
  font-family: monospace;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 2px;
}

.product-category {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 2px;
}

/* Variant Information */
.variant-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.variant-sku {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.8);
  font-weight: 500;
  font-family: monospace;
}

.variant-barcode {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
  font-family: monospace;
}

.no-variant {
  display: flex;
  align-items: center;
  justify-content: center;
}

.variant-chip {
  margin-top: 4px;
}

.profit-margin-chip {
  font-weight: 500;
}

.stock-change-chip {
  font-weight: 500;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-value.blue--text {
  color: #5b39c9 !important;
}

/* Currency breakdown section */
.currency-breakdown {
  background-color: #f5f7fa;
  margin-top: 16px;
  border-radius: 8px;
}

.breakdown-title {
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 16px;
  color: rgba(0, 0, 0, 0.8);
}

.breakdown-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.breakdown-card {
  flex: 1 1 250px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.breakdown-header {
  background-color: #5b39c9;
  color: white;
  padding: 8px 12px;
  font-weight: 500;
}

.breakdown-content {
  padding: 12px;
}

.breakdown-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}

@media (max-width: 960px) {
  .breakdown-card {
    flex: 1 1 100%;
  }

  .history-header {
    padding: 12px;
  }

  .history-content {
    padding: 16px;
  }

  .detail-grid {
    gap: 8px;
  }
}

@media (max-width: 768px) {
  .summary-card {
    margin-top: 16px;
  }

  .info-grid {
    margin-bottom: 16px;
  }

  .items-title {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .history-header {
    padding: 8px;
  }

  .history-content {
    padding: 12px;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .detail-label {
    min-width: auto;
    font-weight: 600;
  }

  .detail-value {
    text-align: left;
    max-width: none;
  }
}

@media (max-width: 600px) {
  .history-header {
    padding: 6px;
  }

  .history-content {
    padding: 8px;
  }

  .detail-section {
    margin-bottom: 16px;
  }

  .detail-title {
    font-size: 0.9rem;
    margin-bottom: 12px;
  }

  .product-name,
  .product-code,
  .product-category {
    font-size: 0.8rem;
  }

  .history-number {
    font-size: 1rem;
  }

  .history-qty {
    font-size: 1rem;
  }

  .history-total {
    font-size: 1rem;
  }
}
</style>
